// @ts-nocheck
/**
 * Content Editor Component
 * Rich text editing interface with Carbon Design System form components
 * Supports section management, template placeholders, and autosave
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  Column,
  Form,
  FormGroup,
  TextInput,
  TextArea,
  Select,
  SelectItem,
  Dropdown,
  Button,
  Toggle,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Tag,
  TagSet,
  InlineNotification,
  Loading,
  Modal,
  Tile,
  FileUploader,
  ProgressIndicator,
  ProgressStep,
  CodeSnippet,
  Accordion,
  AccordionItem,
  InlineLoading,
} from '@carbon/react';
import {
  Save,
  Reset,
  View,
  Code,
  Image,
  Add,
  TrashCan,
  Copy,
  Edit,
  Warning,
  Checkmark,
  Information,
  EventSchedule,
  Time,
} from '@carbon/icons-react';

import type {
  ContentEditorProps,
  ContentEditorState,
  RichTextEditorConfig,
  TemplatePlaceholder,
  ValidationError,
  ContentType,
} from '../../../shared/interfaces/ContentManagementAdmin';

import PublishingScheduler from './PublishingScheduler';

interface Section {
  id: string;
  type: 'text' | 'image' | 'component' | 'code';
  title: string;
  content: any;
  personalizationEnabled: boolean;
  abTestEnabled: boolean;
}

const ContentEditor: React.FC<ContentEditorProps> = ({
  contentId,
  initialContent,
  contentType,
  mode,
  onSave,
  onCancel,
  readOnly = false,
}) => {
  const [state, setState] = useState<ContentEditorState>({
    content: initialContent || {
      title: '',
      description: '',
      sections: [],
      metadata: {
        tags: [],
        type: contentType,
        author: 'current_user', // TODO: Get from auth context
      },
    },
    isDirty: false,
    isSaving: false,
    validationErrors: [],
    autosaveEnabled: true,
  });

  const [activeTab, setActiveTab] = useState('content');
  const [previewMode, setPreviewMode] = useState(false);
  const [placeholderModalOpen, setPlaceholderModalOpen] = useState(false);
  const [publishingSchedulerOpen, setPublishingSchedulerOpen] = useState(false);
  const [schedulingData, setSchedulingData] = useState<any>(null);
  const [newPlaceholder, setNewPlaceholder] = useState<Partial<TemplatePlaceholder>>({});
  const autosaveTimer = useRef<NodeJS.Timeout>();

  // Content type templates
  const contentTemplates = {
    landing: {
      sections: [
        {
          id: '1',
          type: 'text',
          title: 'Hero Section',
          content: '',
          personalizationEnabled: true,
          abTestEnabled: true,
        },
        { id: '2', type: 'text', title: 'Features', content: '', personalizationEnabled: false, abTestEnabled: false },
        { id: '3', type: 'text', title: 'CTA Section', content: '', personalizationEnabled: true, abTestEnabled: true },
      ],
    },
    email: {
      sections: [
        { id: '1', type: 'text', title: 'Header', content: '', personalizationEnabled: true, abTestEnabled: false },
        { id: '2', type: 'text', title: 'Body', content: '', personalizationEnabled: true, abTestEnabled: true },
        { id: '3', type: 'text', title: 'Footer', content: '', personalizationEnabled: false, abTestEnabled: false },
      ],
    },
    popup: {
      sections: [
        { id: '1', type: 'text', title: 'Message', content: '', personalizationEnabled: true, abTestEnabled: true },
        {
          id: '2',
          type: 'component',
          title: 'Actions',
          content: '',
          personalizationEnabled: false,
          abTestEnabled: false,
        },
      ],
    },
    banner: {
      sections: [
        {
          id: '1',
          type: 'text',
          title: 'Banner Content',
          content: '',
          personalizationEnabled: true,
          abTestEnabled: true,
        },
      ],
    },
    feature: {
      sections: [
        {
          id: '1',
          type: 'text',
          title: 'Feature Description',
          content: '',
          personalizationEnabled: true,
          abTestEnabled: false,
        },
      ],
    },
    template: {
      sections: [
        {
          id: '1',
          type: 'text',
          title: 'Template Content',
          content: '',
          personalizationEnabled: true,
          abTestEnabled: true,
        },
      ],
    },
  };

  // Load existing content
  useEffect(() => {
    if (contentId && mode === 'edit') {
      loadContent();
    } else if (mode === 'create') {
      initializeTemplate();
    }
  }, [contentId, mode]);

  // Autosave functionality
  useEffect(() => {
    if (state.autosaveEnabled && state.isDirty && !state.isSaving) {
      autosaveTimer.current = setTimeout(() => {
        handleAutosave();
      }, 5000); // Autosave after 5 seconds of inactivity
    }

    return () => {
      if (autosaveTimer.current) {
        clearTimeout(autosaveTimer.current);
      }
    };
  }, [state.content, state.autosaveEnabled, state.isDirty]);

  const loadContent = async () => {
    if (!contentId) return;

    try {
      const response = await fetch(`/api/v1/content-management/content/${contentId}`);
      if (!response.ok) {
        throw new Error('Failed to load content');
      }

      const data = await response.json();
      setState(prev => ({
        ...prev,
        content: data.content || prev.content,
        isDirty: false,
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        validationErrors: [
          {
            field: 'general',
            message: error instanceof Error ? error.message : 'Failed to load content',
            severity: 'error',
          },
        ],
      }));
    }
  };

  const initializeTemplate = () => {
    const template = contentTemplates[contentType];
    if (template) {
      setState(prev => ({
        ...prev,
        content: {
          ...prev.content,
          sections: template.sections,
        },
      }));
    }
  };

  const handleContentChange = (field: string, value: any) => {
    setState(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [field]: value,
      },
      isDirty: true,
    }));
  };

  const handleSectionChange = (sectionId: string, field: string, value: any) => {
    setState(prev => ({
      ...prev,
      content: {
        ...prev.content,
        sections: prev.content.sections.map((section: Section) =>
          section.id === sectionId ? { ...section, [field]: value } : section
        ),
      },
      isDirty: true,
    }));
  };

  const handleAddSection = (type: Section['type']) => {
    const newSection: Section = {
      id: Date.now().toString(),
      type,
      title: `New ${type} section`,
      content: type === 'code' ? '// Add your code here' : '',
      personalizationEnabled: false,
      abTestEnabled: false,
    };

    setState(prev => ({
      ...prev,
      content: {
        ...prev.content,
        sections: [...prev.content.sections, newSection],
      },
      isDirty: true,
    }));
  };

  const handleRemoveSection = (sectionId: string) => {
    setState(prev => ({
      ...prev,
      content: {
        ...prev.content,
        sections: prev.content.sections.filter((section: Section) => section.id !== sectionId),
      },
      isDirty: true,
    }));
  };

  const handleTagAdd = (tag: string) => {
    if (!state.content.metadata.tags.includes(tag)) {
      setState(prev => ({
        ...prev,
        content: {
          ...prev.content,
          metadata: {
            ...prev.content.metadata,
            tags: [...prev.content.metadata.tags, tag],
          },
        },
        isDirty: true,
      }));
    }
  };

  const handleTagRemove = (tag: string) => {
    setState(prev => ({
      ...prev,
      content: {
        ...prev.content,
        metadata: {
          ...prev.content.metadata,
          tags: prev.content.metadata.tags.filter((t: string) => t !== tag),
        },
      },
      isDirty: true,
    }));
  };

  const validateContent = (): ValidationError[] => {
    const errors: ValidationError[] = [];

    if (!state.content.title?.trim()) {
      errors.push({
        field: 'title',
        message: 'Title is required',
        severity: 'error',
      });
    }

    if (state.content.sections.length === 0) {
      errors.push({
        field: 'sections',
        message: 'At least one section is required',
        severity: 'error',
      });
    }

    // Validate each section
    state.content.sections.forEach((section: Section, index: number) => {
      if (!section.content?.trim()) {
        errors.push({
          field: `section-${section.id}`,
          message: `Section ${index + 1} content is required`,
          severity: 'warning',
        });
      }
    });

    return errors;
  };

  const handleSave = async () => {
    const errors = validateContent();
    setState(prev => ({ ...prev, validationErrors: errors }));

    if (errors.some(error => error.severity === 'error')) {
      return;
    }

    setState(prev => ({ ...prev, isSaving: true }));

    try {
      await onSave(state.content);
      setState(prev => ({
        ...prev,
        isDirty: false,
        isSaving: false,
        validationErrors: [],
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        isSaving: false,
        validationErrors: [
          {
            field: 'general',
            message: error instanceof Error ? error.message : 'Failed to save content',
            severity: 'error',
          },
        ],
      }));
    }
  };

  const handleAutosave = async () => {
    if (!state.isDirty || readOnly) return;

    try {
      // Perform autosave (draft save)
      const response = await fetch('/api/v1/content-management/content/autosave', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contentId: contentId || 'new',
          content: state.content,
        }),
      });

      if (response.ok) {
        setState(prev => ({
          ...prev,
          lastAutosave: new Date().toISOString(),
        }));
      }
    } catch (error) {
      console.error('Autosave failed:', error);
    }
  };

  const handlePreview = () => {
    setPreviewMode(!previewMode);
  };

  const renderContentForm = () => (
    <Form>
      <Grid>
        <Column lg={8} md={6} sm={4}>
          <TextInput
            id="content-title"
            labelText="Title"
            placeholder="Enter content title"
            value={state.content.title}
            onChange={e => handleContentChange('title', e.target.value)}
            invalid={state.validationErrors.some(error => error.field === 'title')}
            invalidText={state.validationErrors.find(error => error.field === 'title')?.message}
            disabled={readOnly}
          />
        </Column>

        <Column lg={4} md={2} sm={2}>
          <Select
            id="content-type"
            labelText="Content Type"
            value={state.content.metadata.type}
            onChange={e =>
              handleContentChange('metadata', {
                ...state.content.metadata,
                type: e.target.value,
              })
            }
            disabled={readOnly || mode === 'edit'}
          >
            <SelectItem value="landing" text="Landing Page" />
            <SelectItem value="email" text="Email Template" />
            <SelectItem value="popup" text="Popup" />
            <SelectItem value="banner" text="Banner" />
            <SelectItem value="feature" text="Feature Content" />
            <SelectItem value="template" text="Template" />
          </Select>
        </Column>

        <Column lg={12} md={6} sm={4}>
          <TextArea
            id="content-description"
            labelText="Description"
            placeholder="Enter content description"
            rows={3}
            value={state.content.description}
            onChange={e => handleContentChange('description', e.target.value)}
            disabled={readOnly}
          />
        </Column>

        <Column lg={4} md={2} sm={2}>
          <FormGroup legendText="Tags">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1rem' }}>
              {state.content.metadata.tags.map((tag: string) => (
                <Tag key={tag} type="blue" onClose={() => handleTagRemove(tag)}>
                  {tag}
                </Tag>
              ))}
            </div>
            <TextInput
              id="tag-input"
              placeholder="Add tag and press Enter"
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  const input = e.target as HTMLInputElement;
                  if (input.value.trim()) {
                    handleTagAdd(input.value.trim());
                    input.value = '';
                  }
                }
              }}
              disabled={readOnly}
            />
          </FormGroup>
        </Column>
      </Grid>
    </Form>
  );

  const renderSectionEditor = (section: Section) => (
    <Accordion key={section.id}>
      <AccordionItem title={section.title}>
        <Grid>
          <Column lg={8} md={6} sm={4}>
            <TextInput
              id={`section-title-${section.id}`}
              labelText="Section Title"
              value={section.title}
              onChange={e => handleSectionChange(section.id, 'title', e.target.value)}
              disabled={readOnly}
            />
          </Column>

          <Column lg={4} md={2} sm={2}>
            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '1.5rem' }}>
              <Toggle
                id={`personalization-${section.id}`}
                labelText="Personalization"
                toggled={section.personalizationEnabled}
                onToggle={toggled => handleSectionChange(section.id, 'personalizationEnabled', toggled)}
                disabled={readOnly}
              />
              <Toggle
                id={`abtest-${section.id}`}
                labelText="A/B Test"
                toggled={section.abTestEnabled}
                onToggle={toggled => handleSectionChange(section.id, 'abTestEnabled', toggled)}
                disabled={readOnly}
              />
            </div>
          </Column>

          <Column lg={12} md={8} sm={4}>
            {section.type === 'code' ? (
              <CodeSnippet type="multi" feedback="Copied to clipboard">
                {section.content}
              </CodeSnippet>
            ) : (
              <TextArea
                id={`section-content-${section.id}`}
                labelText="Content"
                placeholder={`Enter ${section.type} content`}
                rows={6}
                value={section.content}
                onChange={e => handleSectionChange(section.id, 'content', e.target.value)}
                invalid={state.validationErrors.some(error => error.field === `section-${section.id}`)}
                invalidText={state.validationErrors.find(error => error.field === `section-${section.id}`)?.message}
                disabled={readOnly}
              />
            )}
          </Column>

          <Column lg={4} md={4} sm={2}>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <Button
                kind="ghost"
                renderIcon={Copy}
                onClick={() => {
                  const newSection = { ...section, id: Date.now().toString() };
                  setState(prev => ({
                    ...prev,
                    content: {
                      ...prev.content,
                      sections: [...prev.content.sections, newSection],
                    },
                    isDirty: true,
                  }));
                }}
                disabled={readOnly}
              >
                Duplicate
              </Button>
              <Button
                kind="danger--ghost"
                renderIcon={TrashCan}
                onClick={() => handleRemoveSection(section.id)}
                disabled={readOnly}
              >
                Delete
              </Button>
            </div>
          </Column>
        </Grid>
      </AccordionItem>
    </Accordion>
  );

  const renderSectionsEditor = () => (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
        }}
      >
        <h3>Content Sections</h3>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <Button kind="ghost" renderIcon={Edit} onClick={() => handleAddSection('text')} disabled={readOnly}>
            Add Text
          </Button>
          <Button kind="ghost" renderIcon={Image} onClick={() => handleAddSection('image')} disabled={readOnly}>
            Add Image
          </Button>
          <Button kind="ghost" renderIcon={Code} onClick={() => handleAddSection('code')} disabled={readOnly}>
            Add Code
          </Button>
        </div>
      </div>

      {state.content.sections.length === 0 ? (
        <Tile style={{ textAlign: 'center', padding: '2rem' }}>
          <p style={{ color: 'var(--cds-text-02)' }}>No sections yet. Add a section to get started.</p>
        </Tile>
      ) : (
        state.content.sections.map(renderSectionEditor)
      )}
    </div>
  );

  const renderPreview = () => (
    <div
      style={{
        border: '1px solid var(--cds-border-subtle)',
        borderRadius: '4px',
        padding: '2rem',
        backgroundColor: 'var(--cds-layer-01)',
      }}
    >
      <h2>{state.content.title}</h2>
      <p style={{ color: 'var(--cds-text-02)' }}>{state.content.description}</p>

      {state.content.sections.map((section: Section) => (
        <div key={section.id} style={{ marginBottom: '2rem' }}>
          <h3>{section.title}</h3>
          {section.type === 'code' ? (
            <pre
              style={{
                backgroundColor: 'var(--cds-layer-02)',
                padding: '1rem',
                borderRadius: '4px',
                overflow: 'auto',
              }}
            >
              <code>{section.content}</code>
            </pre>
          ) : (
            <div style={{ whiteSpace: 'pre-wrap' }}>{section.content}</div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="content-editor">
      {/* Validation Errors */}
      {state.validationErrors.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          {state.validationErrors.map((error, index) => (
            <InlineNotification
              key={index}
              kind={error.severity === 'error' ? 'error' : 'warning'}
              title="Validation Error"
              subtitle={error.message}
              style={{ marginBottom: '0.5rem' }}
            />
          ))}
        </div>
      )}

      {/* Editor Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          paddingBottom: '1rem',
          borderBottom: '1px solid var(--cds-border-subtle)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <h2>{mode === 'create' ? 'Create Content' : 'Edit Content'}</h2>
          {state.isDirty && (
            <Tag type="blue">
              <Edit size={12} style={{ marginRight: '0.25rem' }} />
              Unsaved changes
            </Tag>
          )}
          {state.lastAutosave && (
            <Tag type="green">
              <Checkmark size={12} style={{ marginRight: '0.25rem' }} />
              Autosaved {new Date(state.lastAutosave).toLocaleTimeString()}
            </Tag>
          )}
        </div>

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <Toggle
            id="autosave-toggle"
            labelText="Autosave"
            toggled={state.autosaveEnabled}
            onToggle={toggled => setState(prev => ({ ...prev, autosaveEnabled: toggled }))}
            disabled={readOnly}
          />
          <Button kind="ghost" renderIcon={View} onClick={handlePreview}>
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
          <Button kind="secondary" renderIcon={Reset} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            kind="secondary"
            renderIcon={Schedule}
            onClick={() => setPublishingSchedulerOpen(true)}
            disabled={readOnly}
          >
            Schedule Publication
          </Button>
          <Button kind="primary" renderIcon={Save} onClick={handleSave} disabled={state.isSaving || readOnly}>
            {state.isSaving ? <InlineLoading description="Saving..." /> : 'Save Content'}
          </Button>
        </div>
      </div>

      {/* Editor Tabs */}
      <Tabs>
        <TabList>
          <Tab>Content</Tab>
          <Tab>Metadata</Tab>
          <Tab>Settings</Tab>
          <Tab>Preview</Tab>
        </TabList>

        <TabPanels>
          <TabPanel>
            {previewMode ? (
              renderPreview()
            ) : (
              <div>
                {renderContentForm()}
                <div style={{ marginTop: '2rem' }}>{renderSectionsEditor()}</div>
              </div>
            )}
          </TabPanel>

          <TabPanel>
            <Grid>
              <Column lg={8} md={6} sm={4}>
                <TextInput
                  id="content-slug"
                  labelText="URL Slug"
                  placeholder="content-url-slug"
                  value={state.content.metadata.slug || ''}
                  onChange={e =>
                    handleContentChange('metadata', {
                      ...state.content.metadata,
                      slug: e.target.value,
                    })
                  }
                  disabled={readOnly}
                />
              </Column>

              <Column lg={4} md={2} sm={2}>
                <Select
                  id="content-status"
                  labelText="Status"
                  value={state.content.metadata.status || 'draft'}
                  onChange={e =>
                    handleContentChange('metadata', {
                      ...state.content.metadata,
                      status: e.target.value,
                    })
                  }
                  disabled={readOnly}
                >
                  <SelectItem value="draft" text="Draft" />
                  <SelectItem value="pending_approval" text="Pending Approval" />
                  <SelectItem value="approved" text="Approved" />
                  <SelectItem value="published" text="Published" />
                </Select>
              </Column>

              <Column lg={12} md={8} sm={4}>
                <TextArea
                  id="content-notes"
                  labelText="Internal Notes"
                  placeholder="Add internal notes about this content"
                  rows={4}
                  value={state.content.metadata.notes || ''}
                  onChange={e =>
                    handleContentChange('metadata', {
                      ...state.content.metadata,
                      notes: e.target.value,
                    })
                  }
                  disabled={readOnly}
                />
              </Column>
            </Grid>
          </TabPanel>

          <TabPanel>
            <Grid>
              <Column lg={12} md={8} sm={4}>
                <Tile style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                    <Schedule size={20} />
                    <Text style={{ fontSize: '1.125rem', fontWeight: 500 }}>Publication Scheduling</Text>
                  </div>

                  {schedulingData ? (
                    <div>
                      <InlineNotification
                        kind="info"
                        title="Scheduled Publication"
                        subtitle={`Content will be published on ${schedulingData.publishDate} at ${schedulingData.publishTime} (${schedulingData.timezone})`}
                        lowContrast
                        hideCloseButton
                      />
                      <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                        <Button kind="ghost" size="sm" onClick={() => setPublishingSchedulerOpen(true)}>
                          Modify Schedule
                        </Button>
                        <Button kind="ghost" size="sm" onClick={() => setSchedulingData(null)}>
                          Remove Schedule
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Text style={{ marginBottom: '1rem' }}>
                        No publication schedule set. Content will be published immediately upon approval.
                      </Text>
                      <Button
                        kind="secondary"
                        size="sm"
                        renderIcon={Time}
                        onClick={() => setPublishingSchedulerOpen(true)}
                      >
                        Set Publication Schedule
                      </Button>
                    </div>
                  )}
                </Tile>
              </Column>

              <Column lg={6} md={4} sm={2}>
                <Toggle
                  id="autosave-setting"
                  labelText="Enable Autosave"
                  toggled={state.autosaveEnabled}
                  onToggle={toggled => setState(prev => ({ ...prev, autosaveEnabled: toggled }))}
                  disabled={readOnly}
                />
              </Column>

              <Column lg={6} md={4} sm={2}>
                <Toggle
                  id="preview-mode-setting"
                  labelText="Preview Mode by Default"
                  toggled={previewMode}
                  onToggle={setPreviewMode}
                  disabled={readOnly}
                />
              </Column>
            </Grid>
          </TabPanel>

          <TabPanel>{renderPreview()}</TabPanel>
        </TabPanels>
      </Tabs>

      {/* Publishing Scheduler Modal */}
      <PublishingScheduler
        contentId={contentId}
        initialSchedule={schedulingData}
        onScheduleSet={schedule => {
          setSchedulingData(schedule);
          // TODO: Save schedule to content metadata
        }}
        onScheduleCancel={() => {
          setSchedulingData(null);
        }}
        isOpen={publishingSchedulerOpen}
        onClose={() => setPublishingSchedulerOpen(false)}
        readOnly={readOnly}
      />
    </div>
  );
};

export default ContentEditor;
